/* eslint-disable @typescript-eslint/no-explicit-any */
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import { bpcApi } from '@core/services/bpc-api.service';
import { Payment } from './../../accounting/interfaces/payment';
import moment from 'moment';
import { BCCashdroResponse, Error } from '@pos/interfaces/cashdro-api-response-interface';
import { BCCashdropResponseInterface } from './../interfaces/cashdro-api-response-interface';
import { CashdroProxyServiceInterface } from './../interfaces/cashdro-proxy-service-interface';
import { Terminal } from '@payment-terminals/interfaces/terminal';
import CashdroProxyService from '@pos/proxy/cashdro-proxy.service';
import CashdroFakeProxyService from '@pos/proxy/cashdro-fake-proxy.service';

export default class CashdroService {
  protected $bpcApiRouteHelper: BPCApiRouteHelper;
  protected $bpcApi: typeof bpcApi;
  constructor() {
    this.$bpcApiRouteHelper = new BPCApiRouteHelper();
    this.$bpcApi = bpcApi;
  }

  genericError: Error = {
    msg: 'payment_terminals.cashdro.error_message.cashdro_generic_exception',
    reason: 'cashdro_generic_exception',
  };

  getProxyInstance(): CashdroProxyServiceInterface {
    let proxy: CashdroProxyServiceInterface = new CashdroProxyService();
    if (process.env.VUE_APP_CASHDRO_TYPE === 'fake') {
      proxy = new CashdroFakeProxyService();
    }
    return proxy;
  }

  async getTerminalStatus(terminal: Terminal): Promise<BCCashdropResponseInterface<any>> {
    const proxy = this.getProxyInstance();
    const response = await proxy.getTerminalStatus(terminal);
    return response;
  }

  async getTerminalUnitsAlerts(terminal: Terminal): Promise<BCCashdropResponseInterface<any>> {
    const proxy = this.getProxyInstance();
    const response = await proxy.getTerminalUnitsAlerts(terminal);
    return response;
  }

  async getTerminalLevels(terminal: Terminal): Promise<BCCashdropResponseInterface<any>> {
    const proxy = this.getProxyInstance();
    const response = await proxy.getTerminalLevels(terminal);
    return response;
  }

  async getOperationStatus(
    terminal: Terminal,
    operationId,
  ): Promise<BCCashdropResponseInterface<any>> {
    const proxy = this.getProxyInstance();
    const response = await proxy.getOperationStatus(terminal, operationId);
    return response;
  }
  /* Encapsulation:  */
  async getTerminalAvailability(terminal: Terminal): Promise<BCCashdropResponseInterface<any>> {
    const proxy = this.getProxyInstance();
    try {
      const executionTerminalOperationsResponse = await proxy.getExecutingTerminalOperations(
        terminal,
      );

      if (executionTerminalOperationsResponse.ko()) {
        if (
          executionTerminalOperationsResponse.getResult().error.reason ===
          'cashdro_operations_in_execution_exception'
        ) {
          const content = executionTerminalOperationsResponse.getResult().content;
          console.log(content);
          const operationId = content.operationId;

          console.log('******************');
          console.log(operationId);
          if (operationId) {
            const executingOperationStatus = await this.getOperationStatus(terminal, operationId);

            if (executingOperationStatus.ko()) {
              return executingOperationStatus;
            }

            const mixedResponse = new BCCashdroResponse(
              false,
              executingOperationStatus.getResult().content,
              executionTerminalOperationsResponse.getResult().error,
            );
            return mixedResponse;
          }
        }
      }
      return executionTerminalOperationsResponse;
    } catch (error) {
      return new BCCashdroResponse(false, error, this.genericError);
    }
  }

  /* Encapsulation */
  async makePayment(terminal: Terminal, amount: number, invoiceId: number, contactId: number) {
    try {
      //Check terminal status
      const terminalStatusResponse = await this.getTerminalStatus(terminal);
      if (terminalStatusResponse.ko()) {
        return terminalStatusResponse;
      }

      //Check terminal Alerts
      const terminalLevelsResponse = await this.getTerminalLevels(terminal);

      if (terminalLevelsResponse.ko()) {
        return terminalLevelsResponse;
      }
      //Check terminal pending operations
      const terminalAvailabilityResponse = await this.getTerminalAvailability(terminal);

      if (terminalAvailabilityResponse.ko()) {
        return terminalAvailabilityResponse;
      }

      const startOperationResponse = await this.startOperation(
        terminal,
        amount,
        invoiceId,
        contactId,
      );
      if (startOperationResponse.ko()) {
        //TODO: check if the error reason is cashdro_busy_with_other_operation_error_exception,
        // And return the operation status.
        // if(startOperationResponse.getResult().error.reason === 'cashdro_busy_with_other_operation_error_exception'){
        // }

        return startOperationResponse;
      }

      const operationId = startOperationResponse.getResult().content.operationId;

      const executeOperationResponse = await this.executeOperation(terminal, operationId);

      if (executeOperationResponse.ko()) {
        return executeOperationResponse;
      }

      /* Create payment in onGoing to store the operationId in bpc-api */
      const payment = await this.createPayment(
        terminal.id,
        operationId,
        amount,
        invoiceId,
        contactId,
      );

      return this.getOperationStatus(terminal, operationId);
    } catch (error) {
      return new BCCashdroResponse(false, error, this.genericError);
    }
  }

  async startOperation(terminal: Terminal, amount: number, invoiceId: number, contactId: number) {
    const proxy = this.getProxyInstance();
    //TODO: check if the conversion for cashdro is well done in all cases.
    const amountRounded = Number(amount.toFixed(2));
    const amountForCashdro = Math.round(amountRounded * 100); // must be an integer

    const response = await proxy.startOperation(terminal, amountForCashdro, invoiceId, contactId);

    return response;
  }

  async executeOperation(terminal: Terminal, operationId) {
    const proxy = this.getProxyInstance();
    const response = await proxy.executeOperation(terminal, operationId);
    return response;
  }

  async createPayment(
    terminalId: number,
    operationId,
    amount: number,
    invoiceId: number,
    contactId: number,
  ) {
    const data = {
      cashdro_id: terminalId,
      invoice_id: invoiceId,
      operation_id: operationId,
      status: 'ongoing',
      payment_date: moment().format('Y-MM-DD'),
      payment_time: moment().format('hh:mm:ss'),
      amount: amount,
      payment_method_type: 'cashdro',
      contact_id: contactId,
      origin: 'pos',
    };
    return await this.savePayment(data);
  }

  public async savePayment(data: AnyEntity): Promise<Payment> {
    const basePath = this.$bpcApiRouteHelper.getEntityPath('payment-terminals', 'raw');
    const res = await this.$bpcApi.post(`${basePath}/cashdro/create-payment`, data).toPromise();
    return res.data;
  }

  async cancelOperation(terminal: Terminal, operationId, invoiceId: number) {
    // Cancel in cashdro
    const proxy = this.getProxyInstance();
    const cancelOperationResponse = await proxy.cancelOperation(terminal, operationId);

    if (cancelOperationResponse.ko()) {
      return cancelOperationResponse;
    }

    try {
      const basePath = this.$bpcApiRouteHelper.getEntityPath('payment-terminals', 'raw');
      const res = await this.$bpcApi
        .post(`${basePath}/cashdro/cancel-payment`, {
          terminal_id: terminal.id,
          operation_id: operationId,
          invoice_id: invoiceId,
        })
        .toPromise();
    } catch (error) {
      console.log('error canceling');
      console.log(error);
      return new BCCashdroResponse(false, error, this.genericError);
    }

    return cancelOperationResponse;
  }

  async finalizeOperation(terminal: Terminal, operationId, invoiceId: number, amount: number) {
    // Finalize in cashdro
    const proxy = this.getProxyInstance();
    const cancelOperationResponse = await proxy.finalizeOperation(terminal, operationId);

    if (cancelOperationResponse.ko()) {
      return cancelOperationResponse;
    }

    try {
      const basePath = this.$bpcApiRouteHelper.getEntityPath('payment-terminals', 'raw');
      const res = await this.$bpcApi
        .post(`${basePath}/cashdro/finalize-payment`, {
          terminal_id: terminal.id,
          operation_id: operationId,
          invoice_id: invoiceId,
          amount: amount,
        })
        .toPromise();
    } catch (error) {
      console.log('error finalizing');
      console.log(error);
      return new BCCashdroResponse(false, error, this.genericError);
    }
    return cancelOperationResponse;
  }

  /* encapsulation */
  async endOperation(invoiceId: number, terminal: Terminal, operationId, amount: number) {
    /* NOTE: import operation in cashdro */
    const proxy = this.getProxyInstance();
    const endOperationResponse = await proxy.importOperation(terminal, operationId);
    if (endOperationResponse.ko()) {
      return endOperationResponse;
    }
    /* NOTE: End payment in Bigcaptain */
    try {
      const basePath = this.$bpcApiRouteHelper.getEntityPath('payment-terminals', 'raw');
      const res = await this.$bpcApi
        .post(`${basePath}/cashdro/finalize-payment`, {
          invoice_id: invoiceId,
          terminal_id: terminal.id,
          operation_id: operationId,
          amount,
        })
        .toPromise();
    } catch (error) {
      console.log('error ending operation');
      console.log(error);
      return new BCCashdroResponse(false, error, this.genericError);
    }

    return endOperationResponse;
  }
}
